import React from 'react';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle.js';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw.js';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useDrawsPage } from './DrawsPageContext';
import { isRolloverWinner } from '../../../utils/winnerDisplay';

export default function DrawsPastList() {
  const { t } = useTranslations();
  const { activeTab, pastDraws, loading } = useDrawsPage();

  if (activeTab !== 'past') return null;

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
        {t('draws.past.heading')}
      </h2>

      <div className="space-y-4">
        {loading ? (
          <div className="rounded-xl p-6 text-gray-900 dark:text-gray-300">
            {t('draws.past.loading')}
          </div>
        ) : pastDraws.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 p-5 rounded-full mb-6">
              <Trophy className="w-12 h-12 text-amber-500 dark:text-amber-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('draws.past.empty.title')}</h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md">
              {t('draws.past.empty.body')}
            </p>
          </div>
        ) : pastDraws.map((draw, index) => (
          draw.type === 'expired' ? (
            <div
              key={draw.id}
              className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-amber-200 dark:border-amber-700/50 hover:shadow-xl transition-all hover:-translate-y-1"
              style={{
                animation: `fade-in-up 0.4s ease-out ${index * 0.05}s both`,
              }}
            >
              <div className="flex flex-col lg:flex-row">
                <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-6 lg:w-64 flex flex-col justify-center items-center text-white">
                  <p className="text-sm font-semibold mb-2 opacity-90">
                    {draw.drawLabel || (draw.drawNumber ? `Draw #${draw.drawNumber}` : 'Draw')}
                  </p>
                  {draw.isPrize ? (
                    <p className="text-2xl font-bold mb-2 text-center leading-tight">
                      {draw.prize || t('draws.past.prizeWon', 'Prize Won')}
                    </p>
                  ) : draw.jackpot > 0 && (
                    <p className="text-4xl font-bold mb-2">${draw.jackpot.toLocaleString()}</p>
                  )}
                  <p className="text-sm opacity-90">{draw.date}</p>
                </div>

                <div className="flex-1 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-semibold">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {t('draws.past.awaitingResults')}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {t('draws.past.awaitingBody')}
                  </p>
                </div>
              </div>
            </div>
          ) : (() => {
          const isRollover = isRolloverWinner(draw);
          return (
          <div
            key={draw.id}
            className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1"
            style={{
              animation: `fade-in-up 0.4s ease-out ${index * 0.05}s both`,
            }}
          >
            <div className="flex flex-col lg:flex-row">
              <div className={`bg-gradient-to-br ${isRollover ? 'from-amber-500 to-orange-500' : 'from-green-600 to-emerald-600'} p-6 lg:w-64 flex flex-col justify-center items-center text-white`}>
                <p className="text-sm font-semibold mb-2 opacity-90">
                  {draw.drawLabel || (draw.drawNumber ? `Draw #${draw.drawNumber}` : 'Draw')}
                </p>
                {draw.isPrize ? (
                  <p className="text-2xl font-bold mb-2 text-center leading-tight">
                    {draw.prize || t('draws.past.prizeWon', 'Prize Won')}
                  </p>
                ) : (
                  <p className="text-4xl font-bold mb-2">${draw.jackpot.toLocaleString()}</p>
                )}
                <p className="text-sm opacity-90">{draw.date}</p>
              </div>

              <div className="flex-1 p-6">
                {isRollover ? (
                  <div className="flex flex-col justify-center h-full">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-bold uppercase tracking-wider w-fit mb-3">
                      <RotateCcw className="w-3.5 h-3.5" />
                      {t('draws.past.rolledOver') || 'Rolled Over'}
                    </span>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                      {t('draws.past.noWinner') || 'No Winner This Draw'}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('draws.past.rolloverBody') || 'The drawn ticket was not eligible, so the jackpot rolled over to the next draw.'}
                    </p>
                  </div>
                ) : (
                <>
                {draw.cardDrawn && (
                  <div className="mb-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                      draw.isWinner
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                    }`}>
                      {draw.isWinner ? (
                        <>
                          <Trophy className="w-3.5 h-3.5" />
                          {t('draws.past.grandPrize') || 'Grand Prize'}
                        </>
                      ) : (
                        <>
                          <TrendingUp className="w-3.5 h-3.5" />
                          {t('draws.past.weeklyWinner') || 'Weekly Winner'}
                        </>
                      )}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      {t('draws.past.winner')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{draw.winner}</p>
                  </div>

                  {draw.isPrize && draw.prize ? (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        {t('draws.past.prizeWon', 'Prize Won')}
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">{draw.prize}</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
                        <Ticket className="w-4 h-4" />
                        {t('draws.past.winningTicket')}
                      </p>
                      <p className="text-lg font-mono font-semibold text-gray-900 dark:text-white">{draw.ticketNumber}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {t('draws.past.totalTicketsSold')}
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {draw.totalTickets != null ? Number(draw.totalTickets).toLocaleString() : '—'}
                    </p>
                  </div>
                </div>
                </>
                )}
              </div>

              {draw.cardDrawn && (() => {
                const suitRaw = String(draw.cardSuit || '').toLowerCase().trim();
                const suitMap = {
                  '♥': { glyph: '♥', red: true },
                  '♦': { glyph: '♦', red: true },
                  '♣': { glyph: '♣', red: false },
                  '♠': { glyph: '♠', red: false },
                  heart: { glyph: '♥', red: true },
                  hearts: { glyph: '♥', red: true },
                  diamond: { glyph: '♦', red: true },
                  diamonds: { glyph: '♦', red: true },
                  club: { glyph: '♣', red: false },
                  clubs: { glyph: '♣', red: false },
                  spade: { glyph: '♠', red: false },
                  spades: { glyph: '♠', red: false }
                };
                const suit = suitMap[suitRaw] || { glyph: draw.cardSuit || '', red: false };
                const rankRaw = String(draw.cardNumber || '').trim();
                const rankMap = { jack: 'J', queen: 'Q', king: 'K', ace: 'A' };
                const rank = rankMap[rankRaw.toLowerCase()] || rankRaw;
                const colorClass = suit.red ? 'text-red-600' : 'text-gray-900';
                return (
                  <div className="bg-gradient-to-br from-gray-900 to-black p-6 lg:w-40 flex flex-col justify-center items-center text-white border-t lg:border-t-0 lg:border-l border-gray-800">
                    <p className="text-xs font-semibold mb-3 opacity-70 uppercase tracking-wider">
                      {t('draws.past.cardDrawn')}
                    </p>
                    <div className={`relative w-20 h-28 rounded-lg bg-white shadow-lg border border-gray-300 ${colorClass}`}>
                      <span className="absolute top-1.5 left-2 text-base font-bold leading-none">{rank}</span>
                      <span className="absolute top-1.5 left-2 mt-4 text-base leading-none">{suit.glyph}</span>
                      <span className="absolute inset-0 flex items-center justify-center text-5xl leading-none">{suit.glyph}</span>
                      <span className="absolute bottom-1.5 right-2 text-base font-bold leading-none rotate-180">{rank}</span>
                      <span className="absolute bottom-1.5 right-2 mb-4 text-base leading-none rotate-180">{suit.glyph}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          );
          })()
        ))}
      </div>
    </div>
  );
}
